import { RollData, DetailedCoin } from '../types';
import { calculateCoinValue, CoinValuation } from './pricing';
import { supabase, getOrCreateSession } from './supabase';
import { COINS_DATA, getCoinById } from '../../data/coins';
import { SetSeriesItem, UserSetStats } from './albumData';

export interface PlayerProfile {
  wallet: number;
  dailyFreeRollsLeft: number;
  lastDailyReset: string;
  lastAllowanceAt?: string;
  totalRollsHunted: number;
  totalCoinsHunted?: number;
  rollsHuntedByDenom?: Record<string, number>;
  isGuest: boolean;
  hideFromLeaderboard?: boolean;
}

export interface AllowanceStatus {
  distinctCoins: number;
  collectionFaceValue?: number;
  allowanceAmount: number;
  isClaimable: boolean;
  lastAllowanceAt?: string;
  nextAllowanceAt?: string;
  secondsRemaining: number;
  nextMilestoneCoins?: number;
  nextMilestoneValue?: number;
  nextTierAllowance?: number;
}

export function calculateAllowanceAmount(collectionFaceValue: number): number {
  return 40 + Math.floor(Math.max(0, collectionFaceValue) / 100) * 5;
}

export interface ActiveRollInfo {
  rollType: string;
  currentIndex: number;
  remainingCoins: number;
  roll: RollData;
}

export interface InventoryCoinItem {
  coin: DetailedCoin;
  count: number;
  valuation: CoinValuation;
  isRare: boolean;
  isCommon: boolean;
  hasDuplicates: boolean;
  duplicateCount: number;
  isLocked?: boolean;
}

export interface InventoryFilterOptions {
  duplicatesOnly?: boolean;
  lockedOnly?: boolean;
  rarities?: string[];
  country?: string;
  denomination?: string;
  sortBy?: 'mintage_asc' | 'mintage_desc' | 'quantity';
  limit?: number;
  offset?: number;
}

export interface InventoryMetaStats {
  totalCoins: number;
  totalDuplicates: number;
  distinctCoins: number;
  collectionFaceValue?: number;
  duplicatesCount: number;
  lockedCount: number;
  totalEstValue: number;
  rarityCounts: {
    'Over 5M': number;
    'U5M': number;
    'U1M': number;
    'U500k': number;
    'U100k': number;
    'NIFC': number;
  };
  countryCounts: Record<string, number>;
  denominationCounts?: Record<string, number>;
  filteredDistinctCoins?: number;
  filteredLockedCount?: number;
  filteredEligibleCount?: number;
  filteredEligiblePayout?: number;
}

export interface FilteredInventoryResult {
  items: InventoryCoinItem[];
  meta: InventoryMetaStats;
}

export interface RollResult {
  roll: RollData;
  currentIndex: number;
  wallet: number;
  dailyFreeRollsLeft: number;
  feeApplied: boolean;
  isSafetyBailout: boolean;
  isResumed?: boolean;
}

export interface RegionalRollWeights {
  base_eurozone_multiplier?: number;
  country_multipliers?: Record<string, number>;
  coin_overrides?: Record<string, number>;
}

export interface RegionalRollRecord {
  id: string;
  name: string;
  country: string;
  flag_emoji: string;
  denomination: string;
  coin_count: number;
  cost: number;
  description: string;
  required_completed_sets: number;
  weights: RegionalRollWeights;
  is_active: boolean;
  sort_order: number;
}

const coinMap = new Map(COINS_DATA.map(c => [c.id, c]));

/**
 * Fetch player profile directly from Supabase
 */
export async function getPlayerProfile(): Promise<PlayerProfile> {
  const defaultProfile: PlayerProfile = {
    wallet: 200.00,
    dailyFreeRollsLeft: 3,
    lastDailyReset: new Date().toISOString(),
    lastAllowanceAt: new Date(Date.now() - 12 * 3600 * 1000).toISOString(),
    totalRollsHunted: 0,
    isGuest: true
  };

  try {
    const session = await getOrCreateSession();
    if (!session) return defaultProfile;

    const { data: profile, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', session.user.id)
      .maybeSingle();

    if (error || !profile) return defaultProfile;

    return {
      wallet: Number(profile.wallet_balance),
      dailyFreeRollsLeft: Number(profile.daily_free_rolls_left),
      lastDailyReset: profile.last_daily_reset,
      lastAllowanceAt: profile.last_allowance_at || defaultProfile.lastAllowanceAt,
      totalRollsHunted: Number(profile.total_rolls_hunted),
      totalCoinsHunted: Number(profile.total_coins_hunted || profile.total_rolls_hunted * 25),
      rollsHuntedByDenom: profile.rolls_hunted_by_denom || { '2euro': Number(profile.total_rolls_hunted) },
      isGuest: Boolean(session.user.is_anonymous),
      hideFromLeaderboard: Boolean(profile.hide_from_leaderboard)
    };
  } catch (err) {
    console.error('Error fetching player profile from Supabase:', err);
    return defaultProfile;
  }
}

/**
 * Fetch current 12-hour allowance status via Supabase RPC
 */
export async function getAllowanceStatus(): Promise<AllowanceStatus | null> {
  try {
    const session = await getOrCreateSession();
    if (!session) return null;

    const { data, error } = await supabase.rpc('get_user_allowance_status', {
      p_user_id: session.user.id
    });

    if (error) {
      console.error('Error fetching allowance status from Supabase:', error);
      return null;
    }

    return data as AllowanceStatus;
  } catch (err) {
    console.error('Error in getAllowanceStatus:', err);
    return null;
  }
}

/**
 * Claim 12-hour allowance via Supabase RPC
 */
export async function claimAllowance(): Promise<{
  success: boolean;
  amountClaimed?: number;
  distinctCoins?: number;
  newWallet?: number;
  nextAllowanceAt?: string;
  error?: string;
}> {
  try {
    const session = await getOrCreateSession();
    if (!session) return { success: false, error: 'No active session' };

    const { data, error } = await supabase.rpc('claim_allowance', {
      p_user_id: session.user.id
    });

    if (error) {
      return { success: false, error: error.message };
    }

    return data || { success: true };
  } catch (err: any) {
    console.error('Error claiming allowance:', err);
    return { success: false, error: err.message || 'Failed to claim allowance' };
  }
}

/**
 * Fetch available regional roll configs directly from Supabase database
 */
export async function getRegionalRolls(): Promise<RegionalRollRecord[]> {
  try {
    const { data, error } = await supabase
      .from('regional_rolls')
      .select('*')
      .eq('is_active', true)
      .order('sort_order', { ascending: true });

    if (error) {
      console.error('Error fetching regional rolls:', error);
      return [];
    }

    return (data || []).map(r => ({
      ...r,
      cost: Number(r.cost)
    }));
  } catch (err) {
    console.error('Error fetching regional rolls from Supabase:', err);
    return [];
  }
}

/**
 * Fetch all active in-progress rolls from Supabase
 */
export async function getActiveRolls(): Promise<Record<string, ActiveRollInfo>> {
  const result: Record<string, ActiveRollInfo> = {};

  try {
    const session = await getOrCreateSession();
    if (!session) return result;

    const { data, error } = await supabase
      .from('active_rolls')
      .select('*')
      .eq('user_id', session.user.id);

    if (!error && data) {
      for (const ar of data) {
        const curIdx = Number(ar.current_index || 0);
        const totalCoins = ar.roll_data?.totalCoins || ar.roll_data?.coins?.length || 25;
        const remaining = Math.max(0, totalCoins - (curIdx + 1));
        result[ar.roll_type] = {
          rollType: ar.roll_type,
          currentIndex: curIdx,
          remainingCoins: remaining,
          roll: ar.roll_data
        };
      }
    }
  } catch (err) {
    console.error('Error fetching active rolls from Supabase:', err);
  }

  return result;
}

/**
 * Helper to fetch all user coin rows safely paginated past the 1,000 row limit
 */
async function fetchAllUserCoins(userId: string): Promise<Array<{ coin_id: string; count: number; is_locked?: boolean }>> {
  const allRows: Array<{ coin_id: string; count: number; is_locked?: boolean }> = [];
  const pageSize = 1000;
  let from = 0;
  let hasMore = true;

  while (hasMore) {
    const { data, error } = await supabase
      .from('user_coins')
      .select('coin_id, count, is_locked')
      .eq('user_id', userId)
      .range(from, from + pageSize - 1);

    if (error || !data || data.length === 0) {
      break;
    }
    allRows.push(...data);
    if (data.length < pageSize) {
      hasMore = false;
    } else {
      from += pageSize;
    }
  }

  return allRows;
}

/**
 * Fetch user inventory items (coins + counts) with full metadata and valuations from Supabase
 */
export async function getUserInventory(): Promise<InventoryCoinItem[]> {
  const rawCounts: Record<string, { count: number; isLocked: boolean }> = {};

  try {
    const session = await getOrCreateSession();
    if (session) {
      const rows = await fetchAllUserCoins(session.user.id);
      for (const c of rows) {
        const existing = rawCounts[c.coin_id] || { count: 0, isLocked: false };
        rawCounts[c.coin_id] = {
          count: existing.count + Number(c.count),
          isLocked: existing.isLocked || Boolean(c.is_locked)
        };
      }
    }
  } catch (err) {
    console.error('Error fetching inventory from Supabase:', err);
  }

  const items: InventoryCoinItem[] = [];
  for (const [id, entry] of Object.entries(rawCounts)) {
    const count = entry.count;
    if (count <= 0) continue;
    const c = coinMap.get(id);
    if (!c) continue;

    const val = calculateCoinValue(c);
    const isRare = c.mintage === 0 || c.mintage <= 5_000_000;
    const isCommon = c.mintage > 5_000_000;
    const duplicateCount = Math.max(0, count - 1);

    items.push({
      coin: {
        id: c.id,
        name: c.name,
        country: c.country,
        year: c.year,
        type: c.type,
        strikeType: c.strikeType || (c.type === 'commemorative' ? 'Commemorative Circulation' : 'Standard Circulation'),
        mintmark: c.mintmark || 'None',
        variety: c.variety,
        mintage: c.mintage,
        mintageFormatted: c.mintageFormatted,
        status: c.status,
        subject: c.subject,
        description: c.description,
        imagePath: c.imagePath
      },
      count,
      valuation: val,
      isRare,
      isCommon,
      isLocked: entry.isLocked,
      hasDuplicates: duplicateCount > 0,
      duplicateCount
    });
  }

  return items;
}

/**
 * Sell 1 coin from inventory via direct Supabase RPC
 */
export async function sellOneCoin(coinId: string): Promise<{
  success: boolean;
  payout: number;
  newWallet: number;
}> {
  try {
    const session = await getOrCreateSession();
    if (session) {
      const { data, error } = await supabase.rpc('sell_coin', {
        p_user_id: session.user.id,
        p_coin_id: coinId
      });
      if (error) throw error;
      if (data && data.success) {
        return { success: true, payout: data.payout, newWallet: data.newWallet };
      }
    }
  } catch (err) {
    console.error('Error selling coin via Supabase:', err);
  }

  return { success: false, payout: 0, newWallet: 0 };
}

/**
 * Deposit 1 coin to bank for €2.00 face value via direct Supabase RPC
 */
export async function depositOneCoin(coinId: string): Promise<{
  success: boolean;
  payout: number;
  newWallet: number;
}> {
  try {
    const session = await getOrCreateSession();
    if (session) {
      const { data, error } = await supabase.rpc('deposit_coin', {
        p_user_id: session.user.id,
        p_coin_id: coinId
      });
      if (error) throw error;
      if (data && data.success) {
        return { success: true, payout: data.payout, newWallet: data.newWallet };
      }
    }
  } catch (err) {
    console.error('Error depositing coin via Supabase:', err);
  }

  return { success: false, payout: 0, newWallet: 0 };
}

/**
 * Bulk deposit all common duplicate coins (>5M mintage) via direct Supabase RPC
 */
export async function depositAllCommonDuplicates(): Promise<{
  success: boolean;
  count: number;
  payout: number;
  newWallet: number;
}> {
  try {
    const session = await getOrCreateSession();
    if (session) {
      const { data, error } = await supabase.rpc('deposit_all_common_duplicates', {
        p_user_id: session.user.id
      });
      if (error) throw error;
      if (data && data.success) {
        return { success: true, count: data.count, payout: data.payout, newWallet: data.newWallet };
      }
    }
  } catch (err) {
    console.error('Error depositing common duplicates via Supabase:', err);
  }

  return { success: false, count: 0, payout: 0, newWallet: 0 };
}

/**
 * Bulk sell all rare duplicate coins (<=5M mintage or NIFC) for market markup via direct Supabase RPC
 */
export async function sellAllRareDuplicates(): Promise<{
  success: boolean;
  count: number;
  payout: number;
  newWallet: number;
}> {
  try {
    const session = await getOrCreateSession();
    if (session) {
      const { data, error } = await supabase.rpc('sell_all_rare_duplicates', {
        p_user_id: session.user.id
      });
      if (error) throw error;
      if (data && data.success) {
        return { success: true, count: data.count, payout: data.payout, newWallet: data.newWallet };
      }
    }
  } catch (err) {
    console.error('Error selling rare duplicates via Supabase:', err);
  }

  return { success: false, count: 0, payout: 0, newWallet: 0 };
}

/**
 * Fetch filtered, sorted user inventory with aggregate metadata from Supabase RPC
 */
export async function getUserInventoryV2(
  filters: InventoryFilterOptions = {}
): Promise<FilteredInventoryResult> {
  const defaultMeta: InventoryMetaStats = {
    totalCoins: 0,
    totalDuplicates: 0,
    distinctCoins: 0,
    collectionFaceValue: 0,
    duplicatesCount: 0,
    lockedCount: 0,
    totalEstValue: 0,
    rarityCounts: {
      'Over 5M': 0,
      'U5M': 0,
      'U1M': 0,
      'U500k': 0,
      'U100k': 0,
      'NIFC': 0
    },
    countryCounts: {},
    denominationCounts: {}
  };

  try {
    const session = await getOrCreateSession();
    if (session) {
      const { data, error } = await supabase.rpc('get_user_inventory_v2', {
        p_user_id: session.user.id,
        p_duplicates_only: Boolean(filters.duplicatesOnly),
        p_locked_only: Boolean(filters.lockedOnly),
        p_rarities: filters.rarities && filters.rarities.length > 0 ? filters.rarities : null,
        p_country: filters.country && filters.country !== 'all' ? filters.country : null,
        p_sort_by: filters.sortBy || 'mintage_asc',
        p_denomination: filters.denomination && filters.denomination !== 'all' ? filters.denomination : null,
        p_limit: filters.limit ?? 100,
        p_offset: filters.offset ?? 0
      });

      if (error) {
        console.error('Error fetching inventory v2 from Supabase:', error);
      } else if (data) {
        const items: InventoryCoinItem[] = (data.items || []).map((raw: any) => {
          const coin = raw.coin || {};
          const valuation: CoinValuation =
            raw.valuation && typeof raw.valuation.marketValue === 'number' && typeof raw.valuation.faceValue === 'number'
              ? raw.valuation
              : calculateCoinValue(coin);

          const isRare =
            raw.isRare ??
            (coin.mintage === 0 ||
              (coin.mintage != null && coin.mintage <= 5_000_000) ||
              valuation.markup > 0 ||
              valuation.marketValue > valuation.faceValue);
          const isCommon = raw.isCommon ?? !isRare;

          return {
            ...raw,
            valuation,
            isRare,
            isCommon
          };
        });

        return {
          items,
          meta: data.meta || defaultMeta
        };
      }
    }
  } catch (err) {
    console.error('Error in getUserInventoryV2:', err);
  }

  // Fallback to client-side reconstruction if offline/guest
  const basicItems = await getUserInventory();
  let filtered = [...basicItems];

  const meta: InventoryMetaStats = {
    totalCoins: basicItems.reduce((sum, it) => sum + it.count, 0),
    totalDuplicates: basicItems.reduce((sum, it) => sum + it.duplicateCount, 0),
    distinctCoins: basicItems.length,
    collectionFaceValue: Number(basicItems.reduce((sum, it) => sum + (it.valuation.faceValue || 2.00), 0).toFixed(2)),
    duplicatesCount: basicItems.filter(it => it.hasDuplicates).length,
    lockedCount: basicItems.filter(it => it.isLocked).length,
    totalEstValue: basicItems.reduce((sum, it) => sum + it.valuation.marketValue * it.count, 0),
    rarityCounts: {
      'Over 5M': basicItems.filter(it => it.valuation.category === 'Common' || (it.coin.mintage && it.coin.mintage > 5000000)).length,
      'U5M': basicItems.filter(it => it.valuation.category === 'U5M').length,
      'U1M': basicItems.filter(it => it.valuation.category === 'U1M').length,
      'U500k': basicItems.filter(it => it.valuation.category === 'U500k').length,
      'U100k': basicItems.filter(it => it.valuation.category === 'U100k').length,
      'NIFC': basicItems.filter(it => it.valuation.category === 'NIFC' || it.coin.mintage === 0).length
    },
    countryCounts: {},
    denominationCounts: {}
  };

  for (const it of basicItems) {
    meta.countryCounts[it.coin.country] = (meta.countryCounts[it.coin.country] || 0) + 1;
    const denom = it.coin.denomination || '2euro';
    meta.denominationCounts![denom] = (meta.denominationCounts![denom] || 0) + 1;
  }

  if (filters.duplicatesOnly) {
    filtered = filtered.filter(it => it.hasDuplicates);
  }
  if (filters.lockedOnly) {
    filtered = filtered.filter(it => it.isLocked);
  }
  if (filters.rarities && filters.rarities.length > 0) {
    filtered = filtered.filter(it => {
      const cat = it.valuation.category === 'Common' ? 'Over 5M' : it.valuation.category;
      return filters.rarities!.includes(cat);
    });
  }
  if (filters.country && filters.country !== 'all') {
    filtered = filtered.filter(it => it.coin.country === filters.country);
  }
  if (filters.denomination && filters.denomination !== 'all') {
    filtered = filtered.filter(it => (it.coin.denomination || '2euro') === filters.denomination);
  }

  if (filters.sortBy === 'mintage_asc') {
    filtered.sort((a, b) => (a.coin.mintage ?? 0) - (b.coin.mintage ?? 0));
  } else if (filters.sortBy === 'mintage_desc') {
    filtered.sort((a, b) => (b.coin.mintage ?? 0) - (a.coin.mintage ?? 0));
  } else if (filters.sortBy === 'quantity') {
    filtered.sort((a, b) => b.count - a.count || (b.coin.mintage ?? 0) - (a.coin.mintage ?? 0));
  }

  // Calculate filtered aggregates before slicing
  const filteredDistinctCoins = filtered.length;
  const filteredLockedCount = filtered.filter(it => it.isLocked).length;
  let filteredEligibleCount = 0;
  let filteredEligiblePayout = 0;

  for (const it of filtered) {
    if (!it.isLocked) {
      const qty = filters.duplicatesOnly ? Math.max(0, it.count - 1) : it.count;
      if (qty > 0) {
        filteredEligibleCount += qty;
        filteredEligiblePayout += qty * (it.valuation.marketValue ?? 2.0);
      }
    }
  }

  meta.filteredDistinctCoins = filteredDistinctCoins;
  meta.filteredLockedCount = filteredLockedCount;
  meta.filteredEligibleCount = filteredEligibleCount;
  meta.filteredEligiblePayout = Number(filteredEligiblePayout.toFixed(2));

  const offset = filters.offset ?? 0;
  const limit = filters.limit ?? 100;
  const slicedItems = filtered.slice(offset, offset + limit);

  return { items: slicedItems, meta };
}

/**
 * Toggle coin lock status
 */
export async function toggleCoinLock(coinId: string): Promise<{
  success: boolean;
  isLocked?: boolean;
  error?: string;
}> {
  try {
    const session = await getOrCreateSession();
    if (!session) return { success: false, error: 'No active session' };

    const { data, error } = await supabase.rpc('toggle_coin_lock', {
      p_user_id: session.user.id,
      p_coin_id: coinId
    });

    if (error) {
      console.error('Error toggling coin lock:', error);
      return { success: false, error: error.message };
    }

    return data || { success: true };
  } catch (err: any) {
    console.error('Error in toggleCoinLock:', err);
    return { success: false, error: err?.message || 'Failed to toggle lock' };
  }
}

/**
 * Set lock status on all filtered coins
 */
export async function setFilteredCoinsLock(
  setLocked: boolean,
  filters: InventoryFilterOptions = {}
): Promise<{
  success: boolean;
  affectedCount?: number;
  isLocked?: boolean;
  error?: string;
}> {
  try {
    const session = await getOrCreateSession();
    if (!session) return { success: false, error: 'No active session' };

    const { data, error } = await supabase.rpc('set_filtered_coins_lock', {
      p_user_id: session.user.id,
      p_set_locked: setLocked,
      p_duplicates_only: Boolean(filters.duplicatesOnly),
      p_rarities: filters.rarities && filters.rarities.length > 0 ? filters.rarities : null,
      p_country: filters.country && filters.country !== 'all' ? filters.country : null,
      p_denomination: filters.denomination && filters.denomination !== 'all' ? filters.denomination : null
    });

    if (error) {
      console.error('Error setting filtered coins lock:', error);
      return { success: false, error: error.message };
    }

    return data || { success: true };
  } catch (err: any) {
    console.error('Error in setFilteredCoinsLock:', err);
    return { success: false, error: err?.message || 'Failed to update locks' };
  }
}

/**
 * Unified bulk deposit/sell for all currently filtered unlocked coins
 */
export async function bulkDepositSellFiltered(
  filters: InventoryFilterOptions = {}
): Promise<{
  success: boolean;
  count: number;
  payout: number;
  newWallet: number;
  error?: string;
}> {
  try {
    const session = await getOrCreateSession();
    if (!session) return { success: false, count: 0, payout: 0, newWallet: 0, error: 'No active session' };

    const { data, error } = await supabase.rpc('bulk_deposit_sell_filtered', {
      p_user_id: session.user.id,
      p_duplicates_only: Boolean(filters.duplicatesOnly),
      p_rarities: filters.rarities && filters.rarities.length > 0 ? filters.rarities : null,
      p_country: filters.country && filters.country !== 'all' ? filters.country : null,
      p_denomination: filters.denomination && filters.denomination !== 'all' ? filters.denomination : null
    });

    if (error) {
      console.error('Error in bulkDepositSellFiltered:', error);
      return { success: false, count: 0, payout: 0, newWallet: 0, error: error.message };
    }

    return data || { success: true, count: 0, payout: 0, newWallet: 0 };
  } catch (err: any) {
    console.error('Error in bulkDepositSellFiltered:', err);
    return { success: false, count: 0, payout: 0, newWallet: 0, error: err?.message || 'Bulk operation failed' };
  }
}

/**
 * Buy or resume a coin roll via Supabase Edge Function (Keeps server-side RNG simulation)
 */
export async function buyRoll(rollType = 'standard_2euro'): Promise<{
  success: boolean;
  data?: RollResult;
  error?: string;
}> {
  try {
    const session = await getOrCreateSession();
    if (!session) {
      return { success: false, error: 'Failed to establish user session' };
    }

    const { data, error } = await supabase.functions.invoke('hunt-roll', {
      headers: { Authorization: `Bearer ${session.access_token}` },
      body: { rollType }
    });

    if (error) {
      let errorMsg = error.message;
      if (error.context && typeof error.context.json === 'function') {
        try {
          const body = await error.context.json();
          if (body.error) errorMsg = body.error;
        } catch {}
      }
      return { success: false, error: errorMsg };
    }

    if (data && data.success) {
      return { success: true, data };
    } else if (data && data.error) {
      return { success: false, error: data.error };
    }

    return { success: false, error: 'Unexpected server response' };
  } catch (err: any) {
    console.error('buyRoll error:', err);
    return { success: false, error: err.message || 'Failed to order roll' };
  }
}

/**
 * Atomically processes a hunted coin via direct Supabase RPC (Keep, Quick Sell, or Quick Deposit).
 * Increments current index, adds kept coins to inventory or credits quick sales to wallet.
 */
export async function processRollCoin(
  rollType: string,
  coinIndex: number,
  coinAction: 'keep' | 'quick_sell' | 'quick_deposit' = 'keep'
): Promise<{
  success: boolean;
  action?: string;
  payout?: number;
  wallet?: number;
  nextIndex?: number;
  isFinished?: boolean;
  error?: string;
}> {
  try {
    const session = await getOrCreateSession();
    if (!session) return { success: false, error: 'No active session' };

    const { data, error } = await supabase.rpc('process_roll_coin', {
      p_user_id: session.user.id,
      p_roll_type: rollType,
      p_coin_index: coinIndex,
      p_action: coinAction
    });

    if (error) {
      console.error('Error in processRollCoin:', error);
      return { success: false, error: error.message };
    }

    return data || { success: true };
  } catch (err: any) {
    console.error('Error processing roll coin in Supabase:', err);
    return { success: false, error: err.message };
  }
}

export interface AlbumOverviewResult {
  userAlbum: Record<string, number>;
  sets: SetSeriesItem[];
  completedSetsCount: number;
}

/**
 * Directly fetch the circulation-completed sets count from Supabase UDF
 */
export async function getCompletedSetsCount(): Promise<number> {
  try {
    const session = await getOrCreateSession();
    if (!session) return 0;

    const { data, error } = await supabase.rpc('get_user_completed_sets_count', {
      p_user_id: session.user.id
    });
    if (!error && typeof data === 'number') {
      return data;
    }
  } catch (err) {
    console.error('Error fetching completed sets count via RPC:', err);
  }
  return 0;
}

// Module cache for sets structure to minimize repeated DB joins
let cachedDbSets: any[] | null = null;

/**
 * Album loader: fetches user coin counts, server-provided sets & series, and completed sets count via direct PostgREST queries
 */
export async function getUserAlbum(): Promise<AlbumOverviewResult> {
  let userAlbum: Record<string, number> = {};
  let sets: SetSeriesItem[] = [];
  let completedSetsCount = 0;

  try {
    const session = await getOrCreateSession();
    if (session) {
      const [albumCountsRes, dbSetsRes, userSetStatsRes] = await Promise.all([
        supabase.rpc('get_user_album_counts', { p_user_id: session.user.id }),
        cachedDbSets
          ? Promise.resolve({ data: cachedDbSets, error: null })
          : supabase
              .from('sets')
              .select('id, title, subtitle, category, header_type, set_coins(coin_id, column_label, column_sublabel, sort_order)')
              .order('sort_order', { ascending: true }),
        supabase.rpc('get_user_completed_sets', { p_user_id: session.user.id })
      ]);

      if (albumCountsRes.data && typeof albumCountsRes.data === 'object') {
        userAlbum = albumCountsRes.data as Record<string, number>;
      } else {
        // Fallback to fetchAllUserCoins if RPC is unavailable
        const allUserCoins = await fetchAllUserCoins(session.user.id);
        for (const uc of allUserCoins) {
          userAlbum[uc.coin_id] = (userAlbum[uc.coin_id] || 0) + Number(uc.count);
        }
      }

      const rawSets = dbSetsRes.data || cachedDbSets || [];
      if (!cachedDbSets && dbSetsRes.data && dbSetsRes.data.length > 0) {
        cachedDbSets = dbSetsRes.data;
      }

      const statsMap = new Map<string, UserSetStats>(
        (userSetStatsRes.data || []).map((st: any) => [st.set_id, st as UserSetStats])
      );

      sets = rawSets.map((s: any) => {
        const sortedSetCoins = (s.set_coins || []).sort((a: any, b: any) => a.sort_order - b.sort_order);
        const mapSetCoin = (sc: any) => {
          const coinId = sc.coin_id || sc.coins?.id;
          const localCoin = getCoinById(coinId);
          return {
            id: coinId,
            country: localCoin?.country || sc.coins?.country || '',
            year: localCoin?.year || sc.coins?.year || 0,
            type: localCoin?.type || sc.coins?.type || 'standard',
            denomination: localCoin?.denomination || sc.coins?.denomination || '2euro',
            mintmark: localCoin?.mintmark ?? sc.coins?.mintmark,
            strikeType: localCoin?.strikeType ?? sc.coins?.strike_type,
            name: localCoin?.name || sc.coins?.name || '',
            subject: localCoin?.subject ?? sc.coins?.subject,
            description: localCoin?.description ?? sc.coins?.description,
            mintage: Number(localCoin?.mintage ?? sc.coins?.mintage ?? 0),
            mintageFormatted: localCoin?.mintageFormatted,
            status: localCoin?.status,
            imagePath: localCoin?.imagePath || sc.coins?.image_key || '',
            image_key: localCoin?.imagePath || sc.coins?.image_key || '',
            isJointIssue: localCoin?.isJointIssue ?? sc.coins?.is_joint_issue
          };
        };

        const coins = sortedSetCoins
          .filter((sc: any) => sc.coin_id != null || sc.coins != null)
          .map((sc: any) => mapSetCoin(sc));

        const columns = sortedSetCoins
          .filter((sc: any) => sc.coin_id != null || sc.coins != null)
          .map((sc: any) => ({
            label: sc.column_label,
            sublabel: sc.column_sublabel || undefined,
            coins: [mapSetCoin(sc)]
          }));

        const setStat = statsMap.get(s.id);
        if (setStat?.is_circ_completed) completedSetsCount++;

        return {
          id: s.id,
          title: s.title,
          subtitle: s.subtitle,
          category: s.category,
          headerType: s.header_type,
          coins,
          columns,
          stats: setStat || null
        };
      });

      return { userAlbum, sets, completedSetsCount };
    }
  } catch (e) {
    console.error('Error fetching album overview from Supabase:', e);
  }

  return { userAlbum, sets, completedSetsCount };
}

/**
 * Fetch leaderboard directly from Supabase UDF (RPC get_leaderboard)
 */
export async function getLeaderboard(
  category: import('../types/leaderboard').LeaderboardCategory
): Promise<import('../types/leaderboard').LeaderboardData | null> {
  try {
    const session = await getOrCreateSession();
    const userId = session?.user?.id || null;

    const { data, error } = await supabase.rpc('get_leaderboard', {
      p_category: category,
      p_user_id: userId
    });

    if (error) {
      console.error('Error fetching leaderboard via RPC:', error);
      return null;
    }

    return data as import('../types/leaderboard').LeaderboardData;
  } catch (err) {
    console.error('Unexpected error fetching leaderboard:', err);
    return null;
  }
}

/**
 * Toggle whether current user's profile is hidden from public leaderboards
 */
export async function setLeaderboardVisibility(hide: boolean): Promise<{
  success: boolean;
  hideFromLeaderboard?: boolean;
  error?: string;
}> {
  try {
    const session = await getOrCreateSession();
    if (!session || !session.user) {
      return { success: false, error: 'User is not authenticated' };
    }

    const { data, error } = await supabase.rpc('set_leaderboard_visibility', {
      p_user_id: session.user.id,
      p_hide: hide
    });

    if (error) {
      console.error('Error updating leaderboard visibility:', error);
      return { success: false, error: error.message };
    }

    return {
      success: true,
      hideFromLeaderboard: Boolean(data?.hide_from_leaderboard)
    };
  } catch (err: any) {
    console.error('Unexpected error in setLeaderboardVisibility:', err);
    return { success: false, error: err?.message || 'Failed to update leaderboard visibility' };
  }
}

/**
 * Claim completion balance reward for a completed set via Supabase RPC
 */
export async function claimSetReward(
  setId: string,
  rewardType = 'circulation'
): Promise<{
  success: boolean;
  setId?: string;
  rewardAmount?: number;
  newWallet?: number;
  error?: string;
}> {
  try {
    const session = await getOrCreateSession();
    if (!session) return { success: false, error: 'No active session' };

    const { data, error } = await supabase.rpc('claim_set_reward', {
      p_user_id: session.user.id,
      p_set_id: setId,
      p_reward_type: rewardType
    });

    if (error) {
      return { success: false, error: error.message };
    }

    return data || { success: true };
  } catch (err: any) {
    console.error('Error claiming set reward:', err);
    return { success: false, error: err.message || 'Failed to claim set reward' };
  }
}

/**
 * Authoritative user achievement item returned by the server
 */
export interface UserAchievementItem {
  id: string;
  name: string;
  description: string;
  tier: import('./achievementsData').AchievementTier;
  reward_amount: number;
  symbol: string;
  current_progress: number;
  max_progress: number;
  is_unlocked: boolean;
  is_claimed: boolean;
  sort_order: number;
}

/**
 * Fetch authoritatively evaluated achievements from PostgreSQL
 */
export async function getUserAchievements(): Promise<UserAchievementItem[]> {
  try {
    const session = await getOrCreateSession();
    if (!session || !session.user) return [];

    const { data, error } = await supabase.rpc('get_user_achievements', {
      p_user_id: session.user.id
    });

    if (error) {
      console.error('Error fetching user achievements:', error);
      return [];
    }

    return (data || []).map((row: any) => ({
      id: row.id,
      name: row.name,
      description: row.description,
      tier: row.tier,
      reward_amount: Number(row.reward_amount),
      symbol: row.symbol,
      current_progress: Number(row.current_progress),
      max_progress: Number(row.max_progress),
      is_unlocked: Boolean(row.is_unlocked),
      is_claimed: Boolean(row.is_claimed),
      sort_order: Number(row.sort_order)
    }));
  } catch (err) {
    console.error('Unexpected error fetching user achievements:', err);
    return [];
  }
}

/**
 * Authoritatively claim achievement balance reward via Supabase RPC (zero client trust)
 */
export async function claimAchievementReward(achievementId: string): Promise<{
  success: boolean;
  achievementId?: string;
  achievementName?: string;
  rewardAmount?: number;
  newWallet?: number;
  error?: string;
}> {
  try {
    const session = await getOrCreateSession();
    if (!session || !session.user) {
      return { success: false, error: 'User is not authenticated' };
    }

    const { data, error } = await supabase.rpc('claim_achievement_reward', {
      p_user_id: session.user.id,
      p_achievement_id: achievementId
    });

    if (error) {
      return { success: false, error: error.message };
    }

    return {
      success: Boolean(data?.success),
      achievementId: data?.achievement_id ?? data?.achievementId,
      achievementName: data?.achievement_name ?? data?.achievementName,
      rewardAmount: Number(data?.reward_amount ?? data?.rewardAmount ?? 0),
      newWallet: data?.new_wallet !== undefined ? Number(data.new_wallet) : (data?.newWallet !== undefined ? Number(data.newWallet) : undefined),
      error: data?.error
    };
  } catch (err: any) {
    console.error('Error claiming achievement reward:', err);
    return { success: false, error: err.message || 'Failed to claim achievement reward' };
  }
}
